import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, Plus } from 'lucide-react';
import Header from './components/Header15';
import Footer from './components/Footer15';
import ProductCard from './components/ProductCard';

const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 10000]);

    const categories = ['All', 'Women', 'Men', 'Essentials', 'New'];
    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest' },
    ];

    const products = [
        {
            id: 1,
            name: 'Essential Tee',
            category: 'Essentials',
            price: 1299,
            image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600',
        },
        {
            id: 2,
            name: 'Minimal Shirt',
            category: 'Men',
            price: 2499,
            image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600',
        },
        {
            id: 3,
            name: 'Clean Lines Dress',
            category: 'Women',
            price: 3999,
            image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600',
        },
        {
            id: 4,
            name: 'Simple Sneakers',
            category: 'Essentials',
            price: 4499,
            image: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600',
        },
        {
            id: 5,
            name: 'Pure Cotton Shirt',
            category: 'Men',
            price: 2799,
            image: 'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=600',
        },
        {
            id: 6,
            name: 'Minimalist Bag',
            category: 'Essentials',
            price: 1899,
            image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600',
        },
        {
            id: 7,
            name: 'Clean Blazer',
            category: 'Women',
            price: 5999,
            image: 'https://images.unsplash.com/photo-1591369822096-ffd140ec948f?w=600',
        },
        {
            id: 8,
            name: 'Essential Boots',
            category: 'New',
            price: 6499,
            image: 'https://images.unsplash.com/photo-1638247025967-b4e38f787b76?w=600',
        },
        {
            id: 9,
            name: 'Timeless Coat',
            category: 'Women',
            price: 8999,
            image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=600',
        },
        {
            id: 10,
            name: 'Simple Watch',
            category: 'Essentials',
            price: 3299,
            image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600',
        },
        {
            id: 11,
            name: 'Pure Denim',
            category: 'Men',
            price: 3499,
            image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=600',
        },
        {
            id: 12,
            name: 'Clean Tote',
            category: 'New',
            price: 2299,
            image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600',
        },
    ];

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());

    return (
        <div className="min-h-screen bg-white">
            <Header cartCount={3} />

            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                {/* Page Header */}
                <div className="mb-16 lg:mb-20">
                    <div className="w-12 h-px bg-gray-900 mb-8"></div>
                    <h1 className="text-5xl lg:text-6xl font-extralight text-gray-900 mb-6">Shop</h1>
                    <p className="text-lg text-gray-600 font-light max-w-2xl">
                        Discover our carefully curated selection of timeless essentials
                    </p>
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col md:flex-row gap-4 mb-12 max-w-4xl">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} strokeWidth={1.5} />
                        <input
                            type="text"
                            placeholder="Search"
                            className="w-full pl-8 pr-4 py-3 bg-transparent border-b border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-900 transition-colors font-light"
                        />
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-0 py-3 bg-transparent border-b border-gray-300 text-gray-900 focus:outline-none focus:border-gray-900 transition-colors font-light"
                    >
                        {sortOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    {/* Filter Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="px-0 py-3 border-b border-gray-300 hover:border-gray-900 transition-colors flex items-center gap-3 font-light text-gray-900"
                    >
                        <SlidersHorizontal size={18} strokeWidth={1.5} />
                        <span className="tracking-wide">Filters</span>
                    </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-gray-50 border border-gray-200 p-8 mb-12 max-w-4xl">
                        <div className="flex justify-between items-center mb-8">
                            <h3 className="font-extralight text-xl tracking-wide">Filter Selection</h3>
                            <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-900 transition-colors">
                                <X size={20} strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Price Range */}
                            <div>
                                <h4 className="font-light mb-4 text-sm text-gray-500 tracking-wider">PRICE</h4>
                                <div className="space-y-3">
                                    <input
                                        type="range"
                                        min="0"
                                        max="10000"
                                        value={priceRange[1]}
                                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                                        className="w-full"
                                    />
                                    <div className="flex justify-between text-sm text-gray-600 font-light">
                                        <span>₹0</span>
                                        <span>₹{priceRange[1]}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Size */}
                            <div>
                                <h4 className="font-light mb-4 text-sm text-gray-500 tracking-wider">SIZE</h4>
                                <div className="flex flex-wrap gap-3">
                                    {['XS', 'S', 'M', 'L', 'XL'].map(size => (
                                        <button
                                            key={size}
                                            className="w-12 h-12 border border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-light text-sm"
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Color */}
                            <div>
                                <h4 className="font-light mb-4 text-sm text-gray-500 tracking-wider">COLOR</h4>
                                <div className="flex flex-wrap gap-3">
                                    {[
                                        { name: 'Black', value: '#000000' },
                                        { name: 'White', value: '#FFFFFF' },
                                        { name: 'Gray', value: '#808080' },
                                        { name: 'Beige', value: '#C0C0C0' }
                                    ].map(color => (
                                        <button
                                            key={color.value}
                                            className="w-12 h-12 border border-gray-300 hover:border-gray-900 transition-colors"
                                            style={{ backgroundColor: color.value }}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Tabs */}
                <div className="flex flex-wrap gap-8 mb-16 justify-center">
                    {categories.map(category => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category.toLowerCase())}
                            className={`text-sm tracking-widest transition-colors pb-2 font-light ${
                                selectedCategory === category.toLowerCase()
                                    ? 'border-b border-gray-900 text-gray-900'
                                    : 'text-gray-500 hover:text-gray-900 border-b border-transparent'
                            }`}
                        >
                            {category.toUpperCase()}
                        </button>
                    ))}
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-12 mb-20">
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>

                {/* Load More */}
                <div className="text-center">
                    <button className="group inline-flex items-center gap-3 border border-gray-300 hover:border-gray-900 px-8 py-3 transition-colors">
                        <span className="text-sm tracking-widest font-light">VIEW ALL</span>
                        <Plus size={16} strokeWidth={1.5} className="group-hover:rotate-90 transition-transform duration-300" />
                    </button>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Shop;
